import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import ContactList from '../../components/contact/ContactList';
import ContactDetail from '../../components/contact/ContactDetail';
import ContactForm from '../../components/contact/ContactForm';
import { Contact, ContactFormData, ContactFilter } from '../../types/contact';
import { useApiService } from '../../hooks/useApiService';

const ContactManagement: React.FC = () => {
  const { contactApi } = useApiService();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<ContactFilter>({});

  // 모달 상태
  const [openForm, setOpenForm] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // 알림 상태
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await contactApi.getContacts();
      if (response.success) {
        setContacts(response.data);
        setFilteredContacts(response.data);
        applyFiltersAndSearch(response.data);
      } else {
        setError(response.message || '담당자 목록을 불러오는 데 실패');
      }
    } catch (err) {
      setError('담당자 목록을 불러오는 데 실패');
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, contactApi]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const applyFiltersAndSearch = useCallback(
    (contactList: Contact[] = contacts) => {
      let result = [...contactList];

      if (searchTerm) {
        const lowerSearchTerm = searchTerm.toLowerCase();
        result = result.filter(
          (contact) =>
            contact.name.toLowerCase().includes(lowerSearchTerm) ||
            contact.role.toLowerCase().includes(lowerSearchTerm) ||
            contact.target.toLowerCase().includes(lowerSearchTerm)
        );
      }

      if (filter.type) {
        result = result.filter((contact) => contact.type === filter.type);
      }

      if (filter.role) {
        result = result.filter(
          (contact) => contact.role.toLowerCase() === filter.role!.toLowerCase()
        );
      }

      if (filter.is_active !== undefined) {
        result = result.filter((contact) => contact.is_active === filter.is_active);
      }

      setFilteredContacts(result);
    },
    [contacts, searchTerm, filter]
  );

  useEffect(() => {
    applyFiltersAndSearch();
  }, [searchTerm, filter, applyFiltersAndSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleFilterChange = (
    e: SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    if (name) {
      setFilter((prev) => ({
        ...prev,
        [name]: value === 'all' ? undefined : value,
      }));
    }
  };

  const handleResetFilters = () => {
    setFilter({});
    setSearchTerm('');
  };

  const handleViewContact = (contact: Contact) => {
    setSelectedContact(contact);
    setOpenDetail(true);
  };

  const handleAddContact = () => {
    setSelectedContact(null);
    setIsEditing(false);
    setOpenForm(true);
  };

  const handleEditContact = (contact: Contact) => {
    setSelectedContact(contact);
    setIsEditing(true);
    setOpenForm(true);
  };

  const handleDeleteConfirm = (id: number) => {
    const contact = contacts.find((c) => c.id === id);
    if (contact) {
      setSelectedContact(contact);
      setOpenDelete(true);
    }
  };

  const handleSubmitContact = async (data: ContactFormData) => {
    try {
      let response;
      if (isEditing && selectedContact) {
        console.warn('Update contact API call is not implemented yet.');
        response = { success: true, data: { ...selectedContact, ...data } };
        if (response.success) {
          setSnackbar({
            open: true,
            message: '담당자 정보가 수정됨',
            severity: 'success',
          });
        }
      } else {
        console.warn('Create contact API call is not implemented yet.');
        const newId = Date.now();
        response = { success: true, data: { id: newId, ...data } };
        if (response.success) {
          setSnackbar({
            open: true,
            message: '담당자가 추가됨',
            severity: 'success',
          });
        }
      }

      if (response.success) {
        setOpenForm(false);
        fetchContacts();
      } else {
        setSnackbar({
          open: true,
          message: response.message || '작업 중 오류 발생',
          severity: 'error',
        });
      }
    } catch (err) {
      console.error('Error submitting contact:', err);
      setSnackbar({
        open: true,
        message: '작업 중 오류 발생',
        severity: 'error',
      });
    }
  };

  const handleDeleteContact = async () => {
    if (!selectedContact) return;

    try {
      console.warn('Delete contact API call is not implemented yet.');
      const response = { success: true };
      if (response.success) {
        setSnackbar({
          open: true,
          message: '연락처가 삭제 완료',
          severity: 'success',
        });
        fetchContacts();
      } else {
        setSnackbar({
          open: true,
          message: '담당자 삭제 중 오류 발생',
          severity: 'error',
        });
      }
    } catch (err) {
      console.error('Error deleting contact:', err);
      setSnackbar({
        open: true,
        message: '담당자 삭제 중 오류 발생',
        severity: 'error',
      });
    } finally {
      setOpenDelete(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        담당자 관리
      </Typography>

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddContact}
        >
          담당자 추가
        </Button>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="담당자 검색..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleResetFilters}
          >
            필터 초기화
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 4, p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          필터
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="type-filter-label">연락 방법</InputLabel>
              <Select
                labelId="type-filter-label"
                name="type"
                value={filter.type || 'all'}
                label="연락 방법"
                onChange={handleFilterChange}
              >
                <MenuItem value="all">전체</MenuItem>
                <MenuItem value="email">이메일</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="app">앱 알림</MenuItem>
                <MenuItem value="webhook">웹훅</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label">상태</InputLabel>
              <Select
                labelId="status-filter-label"
                name="is_active"
                value={filter.is_active === undefined ? 'all' : filter.is_active ? 'active' : 'inactive'}
                label="상태"
                onChange={(e) => {
                  const value = e.target.value;
                  setFilter(prev => ({
                    ...prev,
                    is_active: value === 'all' ? undefined : value === 'active'
                  }));
                }}
              >
                <MenuItem value="all">전체</MenuItem>
                <MenuItem value="active">활성</MenuItem>
                <MenuItem value="inactive">비활성</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 0 }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            담당자 목록
            <Chip
              label={`총 ${filteredContacts.length}명`}
              size="small"
              sx={{ ml: 1 }}
            />
          </Typography>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => fetchContacts()}
          >
            새로고침
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => fetchContacts()}
            >
              다시 시도
            </Button>
          </Box>
        ) : (
          <ContactList
            contacts={filteredContacts}
            onView={handleViewContact}
            onEdit={handleEditContact}
            onDelete={handleDeleteConfirm}
          />
        )}
      </Paper>

      <ContactDetail
        open={openDetail}
        onClose={() => setOpenDetail(false)}
        contact={selectedContact}
      />

      <ContactForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSubmit={handleSubmitContact}
        contact={selectedContact}
        isNew={!isEditing}
      />

      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogContent>
          <DialogContentText>
            {selectedContact?.name} 담당자 삭제 확인. 이 작업은 취소 불가.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>취소</Button>
          <Button onClick={handleDeleteContact} color="error" autoFocus>
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ContactManagement; 